import { useLazyQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from 'react';

import type {
  SearchIntegrationPartnersQuery,
  SearchIntegrationPartnersQueryVariables,
} from 'src/App/generated/graphql-types';
import { SEARCH_INTEGRATION_PARTNERS } from 'src/gql/queries/searchIntegrationPartners';
import type { Partner } from 'src/types';

export type DataState<T> =
  | {
      state: 'data';
      data: T;
    }
  | {
      state: 'loading';
    }
  | {
      state: 'error';
    };

interface State {
  partners: DataState<Partner[]>;
  selectedPartner?: Partner;
  setSelectedPartner: Dispatch<SetStateAction<Partner | undefined>>;
}

const IntegrationPartnerContext = createContext<State | undefined>(undefined);

export const IntegrationPartnerProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  const [partners, setPartners] = useState<DataState<Partner[]>>({
    state: 'loading',
  });
  const [selectedPartner, setSelectedPartner] = useState<Partner>();

  const [queryPartners, { error, loading }] = useLazyQuery<
    SearchIntegrationPartnersQuery,
    SearchIntegrationPartnersQueryVariables
  >(SEARCH_INTEGRATION_PARTNERS, {
    onCompleted: (data) => {
      setPartners({ state: 'data', data: data.searchIntegrationPartners });
    },
    onError: (err) => {
      datadogRum.addError(err, { err });
      setPartners({ state: 'error' });
    },
  });

  useEffect(() => {
    // Only query partners if they haven't been set yet
    if (partners.state === 'loading' && !loading && !error) {
      queryPartners();
    }
  }, [partners, loading, error, queryPartners]);

  return (
    <IntegrationPartnerContext.Provider
      value={{
        partners,
        selectedPartner,
        setSelectedPartner,
      }}
    >
      {children}
    </IntegrationPartnerContext.Provider>
  );
};

export const useIntegrationPartners = () => {
  const context = useContext(IntegrationPartnerContext);
  if (context === undefined) {
    throw new Error(
      'useIntegrationPartners must be used within a IntegrationPartnerProvider',
    );
  }
  return context;
};
